import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '../api';

const AuthContext = createContext(null);

// Username-only accounts live in D1 on the Cloudflare gateway. The synthetic
// email is kept purely so existing UI ("user.email.split('@')[0]") keeps working.
export const EMAIL_DOMAIN = 'newaxis.local';

function toUser(u) {
  return {
    username: u.username,
    displayName: u.displayName || u.username,
    role: u.role,
    email: `${u.username}@${EMAIL_DOMAIN}`,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.me()
      .then((d) => { if (!cancelled) setUser(toUser(d.user)); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (username, password) => {
    const data = await api.login(String(username).trim(), password);
    const next = toUser(data.user);
    setUser(next);
    return next;
  }, []);

  const logout = useCallback(async () => {
    try { await api.logout(); } catch {}
    setUser(null);
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    await api.changePassword(currentPassword, newPassword);
  }, []);

  const isAdmin = Boolean(user && user.role === 'admin');
  const value = { user, isAdmin, loading, login, logout, changePassword };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
